import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Scissors } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1759142235060-3191ee596c81?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Scissors size={20} weight="duotone" />
            </div>
            <div>
              <div className="font-heading text-xl leading-none tracking-tight">Sophie</div>
              <div className="text-[11px] text-muted-foreground tracking-[0.2em] uppercase mt-1">Salon Suite</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl md:text-5xl tracking-tight font-light leading-tight">
            Welcome <br />
            <span className="text-primary">back.</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3">
            Sign in to your salon dashboard.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
                className="bg-[#F9F8F6] border-border h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                className="bg-[#F9F8F6] border-border h-11"
              />
            </div>
            {error && (
              <div
                className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-primary hover:bg-[#2B4F39] text-primary-foreground font-medium"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: image */}
      <div
        className="hidden md:block relative"
        style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="login-image"
      >
        <div className="absolute inset-0 bg-primary/20 backdrop-blur-[1px]" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] font-medium opacity-80">
            Sophie Salon Suite
          </div>
          <div className="font-heading text-3xl mt-2 leading-tight font-light max-w-md">
            The quiet operating system behind your busiest chair.
          </div>
        </div>
      </div>
    </div>
  );
}
